import React from 'react';
import { Platform } from 'react-native';

/**
 * Platform utility functions to ensure cross-platform compatibility
 */

export const isWeb = Platform.OS === 'web';
export const isAndroid = Platform.OS === 'android';
export const isIOS = Platform.OS === 'ios';
export const isNative = isAndroid || isIOS;

/**
 * Safe wrapper for platform-specific imports
 */
export const PlatformUtils = {
  /**
   * Safely import and use react-native-blob-util
   */
  async blobUtil() {
    if (isNative) {
      try {
        const RNBlobUtil = await import('react-native-blob-util');
        return RNBlobUtil.default;
      } catch (error) {
        console.warn('react-native-blob-util not available:', error);
        return null;
      }
    }
    return null;
  },

  /**
   * Safely import and use react-native-html-to-pdf
   */
  async htmlToPdf() {
    if (isNative) {
      try {
        const RNHTMLtoPDF = await import('react-native-html-to-pdf');
        return RNHTMLtoPDF.default;
      } catch (error) {
        console.warn('react-native-html-to-pdf not available:', error);
        return null;
      }
    }
    return null;
  },

  /**
   * Safely import and use expo-local-authentication
   */
  async localAuth() {
    if (isNative) {
      try {
        const LocalAuthentication = await import('expo-local-authentication');
        return LocalAuthentication;
      } catch (error) {
        console.warn('expo-local-authentication not available:', error);
        return null;
      }
    }
    return null;
  },

  /**
   * Safely import and use expo-notifications
   */
  async notifications() {
    if (isNative) {
      try {
        const Notifications = await import('expo-notifications');
        return Notifications;
      } catch (error) {
        console.warn('expo-notifications not available:', error);
        return null;
      }
    }
    return null;
  },

  /**
   * Web-specific utilities
   */
  web: {
    /**
     * Check if running in browser environment
     */
    isBrowser: typeof document !== 'undefined' && typeof window !== 'undefined',

    /**
     * Download file in web browser
     */
    downloadFile: (data: BlobPart, filename: string, mimeType: string) => {
      if (!isWeb || !PlatformUtils.web.isBrowser) {
        throw new Error('downloadFile only available in web browser');
      }
      
      const blob = new Blob([data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },

    /**
     * Open URL in new tab
     */
    openInNewTab: (url: string) => {
      if (!isWeb || !PlatformUtils.web.isBrowser) {
        throw new Error('openInNewTab only available in web browser');
      }
      window.open(url, '_blank');
    }
  },

  /**
   * Native-specific utilities
   */
  native: {
    /**
     * Check if native features are available
     */
    isAvailable: isNative,

    /**
     * Get platform-specific file paths
     */
    getFilePath: (filename: string) => {
      if (!isNative) {
        throw new Error('getFilePath only available on native platforms');
      }
      // This would be implemented with react-native-blob-util
      return filename;
    }
  }
};

/**
 * Conditional rendering helper
 */
export const PlatformWrapper = {
  Web: ({ children }: { children: React.ReactNode }) => 
    isWeb ? React.createElement(React.Fragment, null, children) : null,
  
  Native: ({ children }: { children: React.ReactNode }) => 
    isNative ? React.createElement(React.Fragment, null, children) : null,
  
  Android: ({ children }: { children: React.ReactNode }) => 
    isAndroid ? React.createElement(React.Fragment, null, children) : null,
  
  IOS: ({ children }: { children: React.ReactNode }) => 
    isIOS ? React.createElement(React.Fragment, null, children) : null
};

/**
 * Platform-specific styling
 */
export const platformStyles = {
  shadow: isWeb ? {} : {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  
  // Web-specific styles
  web: {
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  
  // Native-specific styles
  native: {
    // Add any native-specific styles here
  }
};

/**
 * Convenience functions for safe imports
 */
export const getSafeRNBlobUtil = PlatformUtils.blobUtil;
export const getSafeRNHTMLtoPDF = PlatformUtils.htmlToPdf;
export const getSafeLocalAuth = PlatformUtils.localAuth;
export const getSafeNotifications = PlatformUtils.notifications; 